import type { SpinPrize } from './api.js';

/**
 * What to write on a wheel wedge.
 *
 * A prize label is free text an operator typed into the backoffice and stored
 * verbatim in the config, so it cannot be a catalog key in general — whatever
 * they wrote is what every player must read. The two rows a NEW config starts
 * with are the exception: nobody chose that wording, and shipping the seeded
 * English "No win" / "Prize" put untranslated words on a Khmer, Lao, Burmese,
 * Vietnamese and Chinese wheel (#498). The backoffice now seeds a translation
 * key in their place (`spin-prize-rows.ts` over there owns the same strings),
 * and this resolves exactly those keys against the `spin` catalog.
 *
 * A closed map rather than a prefix rule: an unrecognised `luckySpin.prize.*`
 * label would otherwise render its own raw dotted path on the wheel, and
 * `prize-label.test.ts` asserts every value here exists in all seven catalogs,
 * which a runtime-built key could not be checked for.
 *
 * A `Map`, not an object literal, because the label is attacker-adjacent free
 * text: on an object, `'constructor'` / `'toString'` / `'valueOf'` resolve
 * through the prototype chain to a truthy function, which would then be handed
 * to `t()` as if it were a catalog key.
 */
const SEEDED_LABEL_ENTRIES = [
  ['luckySpin.prize.noWin', 'prize.noWin'],
  ['luckySpin.prize.prize', 'prize.prize'],
] as const satisfies ReadonlyArray<readonly [string, string]>;

const SEEDED_LABEL_KEYS = new Map<string, string>(SEEDED_LABEL_ENTRIES);

/** The catalog keys this module can resolve — read by the coverage test. */
export const SEEDED_PRIZE_LABEL_KEYS: ReadonlyArray<readonly [string, string]> =
  SEEDED_LABEL_ENTRIES;

/**
 * `t` must resolve the `spin` namespace. Falls back to the prize key when a
 * config carries no label at all, which is what the wheel did before.
 */
export function prizeLabel(
  prize: Pick<SpinPrize, 'key' | 'label'>,
  t: (key: string) => string,
): string {
  const label = prize.label;
  if (!label) return prize.key;
  const catalogKey = SEEDED_LABEL_KEYS.get(label);
  return catalogKey === undefined ? label : t(catalogKey);
}
